import { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Box,
} from '@mui/material';
import { CheckCircle, MailOutline, InfoOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFirebase } from '../../../hooks';
import { toast } from 'react-toastify';
import { appColors } from "../../../constants/theme";

const EmailVerificationDialog = ({ open, onClose, onConfirm, userEmail }) => {
    const { t } = useTranslation();
    const { sendEmailVerificationFirebase } = useFirebase();
    const [resendLoading, setResendLoading] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [lastSentTime, setLastSentTime] = useState(null);
    const intervalRef = useRef(null);

    const RESEND_COOLDOWN_SECONDS = 60;

    useEffect(() => {
        if (remainingSeconds > 0) {
            intervalRef.current = setInterval(() => {
                setRemainingSeconds((prev) => {
                    if (prev <= 1) {
                        clearInterval(intervalRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [remainingSeconds]);

    useEffect(() => {
        if (open && lastSentTime) {
            const now = Date.now();
            const elapsed = Math.floor((now - lastSentTime) / 1000);
            const remaining = Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
            if (remaining > 0) {
                setRemainingSeconds(remaining);
            }
        }
    }, [open, lastSentTime, RESEND_COOLDOWN_SECONDS]);

    const handleConfirm = () => {
        console.log("handleConfirm");
        onConfirm();
        onClose();
    };

    const handleResendVerification = async () => {
        if (remainingSeconds > 0) {
            toast.error(t("email_verification.wait_before_resend"));
            return;
        }

        try {
            setResendLoading(true);

            let getAuth;
            try {
                const firebaseAuth = await import('firebase/auth');
                getAuth = firebaseAuth.getAuth;
            } catch (importError) {
                console.error("Firebase auth import hatası:", importError);
                toast.error(t("email_verification.firebase_import_error"));
                return;
            }

            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                toast.error(t("email_verification.user_info_not_found"));
                return;
            }

            const result = await sendEmailVerificationFirebase(user);

            if (result.isSuccess) {
                toast.success(t("verification_email_sent"));
                onClose();
            } else {
                const errorCode = result.errorCode;
                const errorMessage = result.errorMessage;

                console.error("Email verification gönderme hatası:", { errorCode, errorMessage });

                const isFirebaseError = errorCode?.startsWith('auth/') || 
                                       errorMessage?.includes('Firebase') ||
                                       errorCode === 'email_verification_no_user';
                
                if (isFirebaseError) {
                    console.error('[EmailVerificationDialog] Firebase hatası (kullanıcıya gösterilmedi):', {
                        errorCode,
                        errorMessage,
                        result
                    });
                    
                    if (errorCode === 'auth/too-many-requests') {
                        setLastSentTime(Date.now());
                        setRemainingSeconds(RESEND_COOLDOWN_SECONDS);
                    }
                } else {
                    toast.error(t("email_verification.verification_email_failed"));
                }
            }
        } catch (error) {
            console.error("Email verification yeniden gönderme hatası:", error);
            toast.error(t("email_verification.verification_email_failed"));
        } finally {
            setResendLoading(false);
        }
    };
    
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',

                    overflow: 'hidden',
                }
            }}
        >
            <Box sx={{
                background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryDark} 100%)`,
                pt: 4,
                pb: 3,
                textAlign: 'center',
            }}>
                <Box sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                }}>
                    <CheckCircle sx={{ fontSize: 48, color: appColors.white }} />
                </Box>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: appColors.white,
                        mb: 0.5,
                        fontSize: '1.4rem',
                    }}
                >
                    {t("email_verification.registration_successful")}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.9rem' }}
                >
                    {t("email_verification.your_account_has_been_successfully_created")}
                </Typography>
            </Box>

            <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
                <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    p: 2,
                    bgcolor: appColors.warningBg,
                    borderRadius: '12px',
                    border: `1px solid ${appColors.warningBg}`,
                    mb: 2.5,
                }}>
                    <InfoOutlined sx={{ color: appColors.warningDark, fontSize: 22, mt: 0.2, flexShrink: 0 }} />
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.warningDark, mb: 0.5, fontSize: '0.85rem' }}>
                            {t("email_verification.email_verification_required")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: appColors.warningDark, lineHeight: 1.6, fontSize: '0.8rem' }}>
                            {t("email_verification.email_verification_required_text")}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    bgcolor: appColors.primaryBg,
                    borderRadius: '12px',
                    border: `1px solid ${appColors.primaryBgPale}`,
                }}>
                    <MailOutline sx={{ color: appColors.primary, fontSize: 22, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: appColors.primaryDeep, lineHeight: 1.5, fontSize: '0.8rem' }}>
                        {t("email_verification.please_check_your_email_and_click_on_the_verification_link")}
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mt: 2,
                    gap: 0.5,
                }}>
                    <Typography
                        variant="body2"
                        sx={{ fontSize: '0.75rem', color: appColors.gray500, textAlign: 'center' }}
                    >
                        {t("email_verification.if_you_have_not_received_the_verification_email")}
                    </Typography>
                    <Button
                        onClick={handleResendVerification}
                        disabled={resendLoading || remainingSeconds > 0}
                        variant="text"
                        size="small"
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            color: appColors.primary,
                            fontWeight: 700,
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            borderRadius: '8px',
                            '&:hover': {
                                color: appColors.primaryDark,
                                bgcolor: 'rgba(13, 148, 136, 0.06)',
                            },
                            '&:disabled': {
                                color: appColors.gray200,
                            }
                        }}
                    >
                        {resendLoading
                            ? t("email_verification.sending")
                            : remainingSeconds > 0
                                ? t("email_verification.resend_in_seconds", { seconds: remainingSeconds })
                                : t("email_verification.resend_verification_email")
                        }
                    </Button>
                </Box>

                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    fullWidth
                    disableElevation
                    sx={{
                        mt: 2.5,
                        mb: 1,
                        height: '48px',
                        borderRadius: '12px',
                        bgcolor: appColors.primary,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        letterSpacing: '0.02em',

                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: appColors.primaryDark,

                            transform: 'translateY(-1px)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        }
                    }}
                >
                    {t("email_verification.ok")}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default EmailVerificationDialog;
